import { ObjectWithClientId, setClientId } from "client/Misc/clientId";
import { never } from "client_legacy/ndaw/errors";
import {
	FrequentlyAskedQuestionItemViewModel,
	getHubImageUrl,
	HubItemElementKind,
	HubItemMediaKind
} from "client/Api/Hub/common";
import {
	HubItemColumnComposerViewModel,
	HubItemElementComposerViewModel,
	HubItemLayoutComposerViewModel,
	HubItemRowComposerViewModel,
	BlobIdImageElementComposerViewModel,
	HubImageMediaKind,
	HubItemMediaComposerViewModel,
	HubItemMediaSaveModel,
	NoneHubMediaSaveModel,
	ImageHubMediaSaveModel,
	VideoHubMediaSaveModel,
	EmbedHubMediaSaveModel,
	BlobIdImageHubMediaComposerViewModel,
	HubItemLayoutSaveModel,
	HubItemRowSaveModel,
	HubItemColumnSaveModel,
	HubItemElementSaveModel,
	FrequentlyAskedQuestionItemSaveModel
} from "client/Api/Hub/common.composer";
import { isNil, isNotNil } from "client/Misc/utils";
import {
	HubItemColumnFormModel,
	HubItemElementFormModel,
	HubItemLayoutFormModel,
	HubItemMediaFormModel,
	HubItemRowFormModel
} from "./Management/common";
import {
	Base64FileUriUploadWithMetadataAndAttributionModel,
	fileMetadataSymbol
} from "client/Misc/commonTypes";
import { MediaContentModel } from "./HubDetailsLayout";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	HubItemColumnContentViewerModel,
	HubItemElementContentViewerModel,
	HubItemLayoutContentViewerModel,
	HubItemRowContentViewerModel
} from "./Builder/Viewer/ContentViewer";
import { TagSaveModel, TagViewModel } from "client/Api/Tags/tags";
import { TagModel } from "client/Components";
import { hubItemKindToHubDataSourceMap } from "./Sidebar/common";
import { DEFAULT_FOCAL_POINT } from "./FocalPointDialog/FocalPointEditorSection";
import {
	ImageAttributionKind,
	ImageAttributionSaveModel,
	ImageAttributionViewModel
} from "client/Api/common";

export function getOriginalImagesFromLayout(layout: HubItemLayoutComposerViewModel): Map<number, BlobIdImageElementComposerViewModel> {
	const result = new Map<number, BlobIdImageElementComposerViewModel>();

	for (const row of layout.rows) {
		for (const column of row.columns) {
			for (const element of column.elements) {
				if (isNotNil(element.id)
					&& element.kind === HubItemElementKind.Image
					&& element.imageKind === HubImageMediaKind.BlobId
				) {
					result.set(element.id, element);
				}
			}
		}
	}

	return result;
}

export async function convertLayoutToSaveModel(layout: HubItemLayoutComposerViewModel): Promise<HubItemLayoutFormModel> {
	const rows: ObjectWithClientId<HubItemRowFormModel>[] = [];

	for (const row of layout.rows) {
		rows.push(setClientId(await convertRowToSaveModel(row)));
	}

	return {
		rows
	};
}

async function convertRowToSaveModel(row: HubItemRowComposerViewModel): Promise<HubItemRowFormModel> {
	const columns: ObjectWithClientId<HubItemColumnFormModel>[] = [];

	for (const column of row.columns) {
		columns.push(
			setClientId(await convertColumnToSaveModel(column))
		);
	}

	return {
		layout: row.layout,
		columns
	};
}

async function convertColumnToSaveModel(column: HubItemColumnComposerViewModel): Promise<HubItemColumnFormModel> {
	const elements: ObjectWithClientId<HubItemElementFormModel>[] = [];

	for (const element of column.elements) {
		elements.push(
			setClientId(await convertElementToSaveModel(element))
		);
	}

	return {
		elements
	};
}

async function convertElementToSaveModel(element: HubItemElementComposerViewModel): Promise<HubItemElementFormModel> {
	switch(element.kind)	{
		case HubItemElementKind.HorizontalLine:
			return {
				kind: HubItemElementKind.HorizontalLine,
				id: element.id
			};
		case HubItemElementKind.Image:
			return {
				kind: HubItemElementKind.Image,
				id: element.id,
				image: element.imageKind === HubImageMediaKind.Inline
					? await getImageWithMetadataAndAttribution(
						element.dataUri,
						element.name,
						element.attribution
					)
					: element.imageKind === HubImageMediaKind.BlobId
						? { isSet: false }
						: never(element)
			};
		case HubItemElementKind.Paragraph:
			return {
				kind: HubItemElementKind.Paragraph,
				id: element.id,
				html: element.html ?? ""
			};
		case HubItemElementKind.Subtitle:
			return {
				kind: HubItemElementKind.Subtitle,
				id: element.id,
				text: element.text
			};
		case HubItemElementKind.Video:
			return {
				kind: HubItemElementKind.Video,
				id: element.id,
				url: element.url
			};
		case HubItemElementKind.Embed:
			return {
				kind: HubItemElementKind.Embed,
				id: element.id,
				code: element.code
			};
		case HubItemElementKind.FrequentlyAskedQuestion:
			return {
				kind: HubItemElementKind.FrequentlyAskedQuestion,
				id: element.id,
				items: element.items
					.map(setClientId)
			};
		default:
			never(element);
	}
}

export async function convertMediaToSaveModel(media: HubItemMediaComposerViewModel): Promise<HubItemMediaFormModel> {
	const commonMediaObject: HubItemMediaFormModel = createCommonMediaObject();
	let specificMediaObject: HubItemMediaFormModel;

	switch (media.mediaKind) {
		case HubItemMediaKind.None:
			specificMediaObject = {
				mediaKind: HubItemMediaKind.None
			};
			break;
		case HubItemMediaKind.Image:
			specificMediaObject = {
				mediaKind: HubItemMediaKind.Image,
				focalPoint: media.focalPoint,
				image: media.imageKind === HubImageMediaKind.Inline
					? await getImageWithMetadataAndAttribution(
						media.dataUri,
						media.imageName,
						media.attribution
					)
					: media.imageKind === HubImageMediaKind.BlobId
						? { isSet: false }
						: never(media)
			};
			break;
		case HubItemMediaKind.Video:
			specificMediaObject = {
				mediaKind: HubItemMediaKind.Video,
				videoUrl: media.videoUrl
			};
			break;
		case HubItemMediaKind.Embed:
			specificMediaObject = {
				mediaKind: HubItemMediaKind.Embed,
				embedCode: media.embedCode
			};
			break;
		default:
			never(media);
	}

	return {
		...commonMediaObject,
		...specificMediaObject
	};
}

async function getImageWithMetadataAndAttribution(
	dataUri: string,
	fileName: string,
	attribution: ImageAttributionViewModel | null
): Promise<OptionalModel<Base64FileUriUploadWithMetadataAndAttributionModel>> {
	const { fileSize, fileType } = await getFileInfoFromUri(dataUri);

	return {
		isSet: true,
		value: {
			filename: fileName,
			uri: dataUri,
			[fileMetadataSymbol]: {
				size: fileSize,
				mimeType: fileType
			},
			attribution: attribution
				? convertToAttributionSaveModel(attribution)
				: null
		}
	};
}

export function convertToAttributionSaveModel(
	attribution: ImageAttributionViewModel
): ImageAttributionSaveModel {
	switch (attribution.kind) {
		case ImageAttributionKind.Pixabay:
			return {
				kind: ImageAttributionKind.Pixabay,
				pixabay: {
					authorId: attribution.authorId,
					authorName: attribution.authorName
				}
			};
		default:
			never(attribution.kind);
	}
}

export function convertToAttributionViewModel(
	attribution: ImageAttributionSaveModel
): ImageAttributionViewModel {
	switch (attribution.kind) {
		case ImageAttributionKind.Pixabay:
			const { pixabay } = attribution;

			return {
				kind: ImageAttributionKind.Pixabay,
				authorId: pixabay.authorId,
				authorName: pixabay.authorName
			};
		default:
			never(attribution.kind);
	}
}

export function createCommonMediaObject(): HubItemMediaFormModel {
	const noneMedia: NoneHubMediaSaveModel = {
		mediaKind: HubItemMediaKind.None
	};

	const imageMedia: ImageHubMediaSaveModel = {
		mediaKind: HubItemMediaKind.Image,
		image: {
			isSet: false
		},
		focalPoint: DEFAULT_FOCAL_POINT
	};

	const videoMedia: VideoHubMediaSaveModel = {
		mediaKind: HubItemMediaKind.Video,
		videoUrl: ""
	};

	const embedMedia: EmbedHubMediaSaveModel = {
		mediaKind: HubItemMediaKind.Embed,
		embedCode: ""
	};

	return {
		...noneMedia,
		...imageMedia,
		...videoMedia,
		...embedMedia
	};
}

export function cleanUpMedia(media: HubItemMediaFormModel): HubItemMediaSaveModel {
	switch(media.mediaKind) {
		case HubItemMediaKind.None:
			return {
				mediaKind: HubItemMediaKind.None
			};
		case HubItemMediaKind.Image:
			return {
				mediaKind: HubItemMediaKind.Image,
				image: media.image,
				focalPoint: media.focalPoint
			};
		case HubItemMediaKind.Video:
			return {
				mediaKind: HubItemMediaKind.Video,
				videoUrl: media.videoUrl
			};
		case HubItemMediaKind.Embed:
			return {
				mediaKind: HubItemMediaKind.Embed,
				embedCode: media.embedCode
			};
		default:
			never(media);
	}
}

export function convertMediaForDetails(
	model: HubItemMediaSaveModel,
	itemKind: HubItemKind,
	originalImage: BlobIdImageHubMediaComposerViewModel | null
): MediaContentModel {
	switch (model.mediaKind) {
		case HubItemMediaKind.Image:
			if (model.image.isSet) {
				const { attribution } = model.image
					.value;

				return {
					mediaKind: HubItemMediaKind.Image,
					attribution: isNotNil(attribution)
						? convertToAttributionViewModel(attribution)
						: null,
					imageUrl: model.image
						.value
						.uri
				};
			}

			if (!originalImage) {
				return {
					mediaKind: HubItemMediaKind.None
				};
			}

			return {
				mediaKind: HubItemMediaKind.Image,
				attribution: originalImage.attribution,
				imageUrl: getHubImageUrl(
					hubItemKindToHubDataSourceMap[itemKind],
					originalImage.imageId
				)
			};

		default:
			return model;
	}
}

export function convertLayoutForDetails(
	layout: HubItemLayoutSaveModel,
	originalImages: Map<number, BlobIdImageElementComposerViewModel>
): HubItemLayoutContentViewerModel {
	let newId: number = 0;

	const getNewId = (): number => {
		return --newId;
	};

	const convertFaqItem = (item: FrequentlyAskedQuestionItemSaveModel): FrequentlyAskedQuestionItemViewModel => {
		return {
			question: item.question,
			answer: item.answer
		};
	};

	const convertElement = (element: HubItemElementSaveModel): HubItemElementContentViewerModel => {
		switch (element.kind) {
			case HubItemElementKind.Subtitle:
				return {
					kind: HubItemElementKind.Subtitle,
					id: element.id
						?? getNewId(),
					text: element.text
				};
			case HubItemElementKind.Paragraph:
				return {
					kind: element.kind,
					id: element.id
						?? getNewId(),
					html: element.html
				};
			case HubItemElementKind.HorizontalLine:
				return {
					kind: element.kind,
					id: element.id
						?? getNewId()
				};
			case HubItemElementKind.FrequentlyAskedQuestion:
				return {
					kind: element.kind,
					id: element.id
						?? getNewId(),
					items: element.items
						.map(convertFaqItem)
				};
			case HubItemElementKind.Embed:
				return {
					kind: element.kind,
					id: element.id
						?? getNewId(),
					code: element.code
				};
			case HubItemElementKind.Video:
				return {
					kind: element.kind,
					id: element.id
						?? getNewId(),
					url: element.url
				};
			case HubItemElementKind.Image:
				if (element.image.isSet) {
					const { attribution } = element.image
						.value;

					return {
						kind: HubItemElementKind.Image,
						id: element.id
							?? getNewId(),
						name: element.image
							.value
							.filename,
						uri: element.image
							.value
							.uri,
						attribution: isNotNil(attribution)
							? convertToAttributionViewModel(attribution)
							: null
					};
				} else {
					if (isNil(element.id)) {
						return {
							id: getNewId(),
							kind: HubItemElementKind.Image,
							value: "NONE"
						};
					}

					const image: BlobIdImageElementComposerViewModel | undefined = originalImages.get(element.id);

					if (!image) {
						return {
							id: element.id,
							kind: HubItemElementKind.Image,
							value: "NONE"
						};
					}

					return {
						kind: HubItemElementKind.Image,
						id: element.id,
						blobId: image.blobId,
						name: image.name,
						attribution: image.attribution
					};
				}
			default:
				never(element);
		}
	};

	const convertColumn = (column: HubItemColumnSaveModel): HubItemColumnContentViewerModel => {
		return {
			elements: column.elements.map(convertElement)
		};
	};

	const convertRow = (row: HubItemRowSaveModel): HubItemRowContentViewerModel => {
		return {
			layout: row.layout,
			columns: row.columns.map(convertColumn)
		};
	};

	return {
		rows: layout.rows.map(convertRow)
	};
};

export function convertTagForDetails(tag: TagSaveModel | TagViewModel): TagModel<number> {
	return {
		text: tag.name,
		value: tag.id,
		color: "secondary"
	};
}

type FileInfo = {
	fileType: string;
	fileSize: number;
};

async function getFileInfoFromUri(uri: string): Promise<FileInfo>{
	const urlObj = new URL(uri);

	if (urlObj.protocol !== "data:") {
		throw new Error("The provided uri does not have a data: protocol.");
	}

	const blob: Blob = await fetch(urlObj).then(res => res.blob());

	return {
		fileType: blob.type,
		fileSize: blob.size
	};
}